if (!customElements.get('bulk-order-configurator')) {
  /**
   * The Back in Stock app (Stok) renders a single product-level trigger. Its markup is
   * server-rendered but its click handler is attached later by the app's own bundle, so the
   * per-row Notify buttons start disabled and are only enabled once the app looks ready.
   * If these ids ever change, this is the only place to update them.
   */
  const NOTIFY_TRIGGER_SELECTOR = '#bis-myBtn';
  const NOTIFY_WRAPPER_SELECTOR = '#bis_notify_me_btn';
  const NOTIFY_SELECTED_VARIANT_SELECTOR = '#selected_variant';
  const NOTIFY_POLL_INTERVAL = 500;
  const NOTIFY_POLL_ATTEMPTS = 120;
  const NOTIFY_SELECT_POLL_INTERVAL = 100;
  const NOTIFY_SELECT_POLL_ATTEMPTS = 30;
  const ROW_NOTIFY_READY_CLASS = 'is-notify-ready';

  const log = (...args) => console.log('Bulk Order Configurator Says :', ...args); // eslint-disable-line no-console

  class BulkOrderConfigurator extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      const payloadEl = this.querySelector('script[data-variants]');
      if (!payloadEl) return;

      try {
        this.variants = JSON.parse(payloadEl.textContent);
      } catch (e) {
        console.error('bulk-order-configurator: invalid JSON payload', e);
        return;
      }
      if (!Array.isArray(this.variants) || this.variants.length === 0) return;

      this.selections = {};
      this.cartQtys = {};
      this.variants.forEach((v) => { this.selections[v.variantId] = 0; });

      this.rows = this.querySelectorAll('[data-row]');
      this.totalEl = this.querySelector('[data-bulk-order-total]');
      this.addBtn = this.querySelector('[data-bulk-order-add]');
      this.errorEl = this.querySelector('[data-bulk-order-error]');

      // Liquid decides out/backorder/unavailable at render time; those rows stay in that
      // state for the life of the page. Only 'ok' rows can change (to 'maxed') as the cart moves.
      this.initialStates = {};
      this.rows.forEach((row) => {
        this.initialStates[row.dataset.variantId] = row.dataset.stockState;
      });

      this.bindRows();
      this.bindAdd();
      this.bindNotify();
      this.refreshCart().then(() => this.recomputeAll());
    }

    bindRows() {
      this.rows.forEach((row) => {
        const minus = row.querySelector('[data-qty-minus]');
        const plus = row.querySelector('[data-qty-plus]');
        const input = row.querySelector('[data-qty-input]');
        const clear = row.querySelector('[data-qty-clear]');

        minus?.addEventListener('click', () => this.changeQty(row, -1));
        plus?.addEventListener('click', () => this.changeQty(row, 1));
        input?.addEventListener('input', () => this.onInputChange(row));
        input?.addEventListener('change', () => this.onInputChange(row));
        clear?.addEventListener('click', () => this.setRowQty(row, 0));
      });
    }

    bindAdd() {
      this.addBtn?.addEventListener('click', () => this.handleAdd());
    }

    /**
     * Rows render with the plain "Out of stock" badge and a hidden, disabled Notify button.
     * Nothing is swapped in — and the app's own button is left alone — until we have confirmed
     * the app is actually there to receive the click.
     */
    bindNotify() {
      this.notifyBtns = Array.from(this.querySelectorAll('[data-notify-btn]'));

      const outRows = Array.from(this.rows).filter((row) => row.dataset.stockState === 'out');
      if (outRows.length === 0) {
        log('No out of stock variants found — nothing to do');
        return;
      }

      log(`Found ${outRows.length} out of stock variant(s)`);

      if (this.notifyBtns.length === 0) return;

      this.notifyBtns.forEach((btn) => {
        btn.addEventListener('click', () => this.openNotify(btn.closest('[data-row]')?.dataset.variantId));
      });

      log(`Searching for Stok's application notify me button (${NOTIFY_TRIGGER_SELECTOR} inside ${NOTIFY_WRAPPER_SELECTOR})`);
      this.awaitNotifyTrigger(0);
    }

    /**
     * Polls for the app's trigger. The wrapper's inline display is set by the app when it decides
     * the button should show, so waiting on it gets us as close to "handler attached" as we can
     * see from outside the app.
     * @param {number} attempt - current poll attempt
     */
    awaitNotifyTrigger(attempt) {
      const triggers = document.querySelectorAll(NOTIFY_TRIGGER_SELECTOR);
      const wrapper = document.querySelector(NOTIFY_WRAPPER_SELECTOR);

      if (triggers.length > 0 && wrapper && wrapper.style.display !== 'none') {
        if (triggers.length > 1) {
          log(`Multiple notify me buttons are placed (${triggers.length}) — using the first`);
        }
        log("Stok's application notify me button found");
        this.notifyTrigger = triggers[0];
        this.showNotifyButtons();
        return;
      }

      if (attempt >= NOTIFY_POLL_ATTEMPTS) {
        log("Stok's application notify me button not found — keeping the out of stock badges");
        this.notifyBtns.forEach((btn) => btn.remove());
        this.notifyBtns = [];
        return;
      }

      setTimeout(() => this.awaitNotifyTrigger(attempt + 1), NOTIFY_POLL_INTERVAL);
    }

    /**
     * Opens the app's popup for one specific variant. The app reads the variant from its own
     * hidden input, which is empty on this template because there is no variant picker to fill
     * it, so we point it at the row that was clicked before handing over.
     * @param {string} variantId - variant of the row whose button was clicked
     */
    openNotify(variantId) {
      if (!this.notifyTrigger) return;

      // Tracks the most recent open so a slow lookup from an earlier click cannot come back and
      // overwrite the dropdown after the customer has clicked a different row.
      this.notifyRequest = (this.notifyRequest || 0) + 1;

      const selected = document.querySelector(NOTIFY_SELECTED_VARIANT_SELECTOR);
      if (selected && variantId) {
        selected.value = variantId;
        selected.dispatchEvent(new Event('change', { bubbles: true }));
        log(`Preselecting variant ${variantId}`);
      }

      this.notifyTrigger.click();

      if (variantId) this.awaitPopupSelect(variantId, 0, this.notifyRequest);
    }

    /**
     * The popup is built after the click and its variant dropdown may be filled asynchronously,
     * so wait for an option matching this variant before selecting it.
     * @param {string} variantId - variant to select
     * @param {number} attempt - current poll attempt
     * @param {number} requestId - the open this lookup belongs to
     */
    awaitPopupSelect(variantId, attempt, requestId) {
      if (requestId !== this.notifyRequest) return;

      const select = this.findPopupSelect(variantId);
      if (select) {
        select.value = String(variantId);
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        log(`Popup variant dropdown set to ${variantId}`);
        return;
      }

      if (attempt >= NOTIFY_SELECT_POLL_ATTEMPTS) {
        log('Popup variant dropdown not found — the customer will need to pick the variant');
        return;
      }

      setTimeout(() => this.awaitPopupSelect(variantId, attempt + 1, requestId), NOTIFY_SELECT_POLL_INTERVAL);
    }

    /**
     * Finds the popup's variant dropdown by looking for a visible select, outside this component,
     * that actually offers the variant we want — safer than guessing at the app's markup.
     * @param {string} variantId - variant the dropdown must offer
     * @returns {?HTMLSelectElement}
     */
    findPopupSelect(variantId) {
      return Array.from(document.querySelectorAll('select')).find((select) => (
        !this.contains(select)
        && select.offsetParent !== null
        && Array.from(select.options).some((option) => option.value === String(variantId))
      ));
    }

    /**
     * Swaps each sold-out row from badge to button. The app's own button is deliberately left
     * visible: until the proxied click is proven, it is the customer's working fallback.
     */
    showNotifyButtons() {
      this.notifyBtns.forEach((btn) => {
        btn.disabled = false;
        btn.closest('[data-row]')?.classList.add(ROW_NOTIFY_READY_CLASS);
      });
    }

    async refreshCart() {
      try {
        const res = await fetch(`${theme.routes.cart}.js`);
        if (!res.ok) return;
        const data = await res.json();
        this.cartQtys = {};
        (data.items || []).forEach((line) => {
          this.cartQtys[line.variant_id] = (this.cartQtys[line.variant_id] || 0) + line.quantity;
        });
      } catch (e) {
        this.cartQtys = {};
      }
    }

    changeQty(row, delta) {
      const input = row.querySelector('[data-qty-input]');
      const current = parseInt(input.value, 10) || 0;
      this.setRowQty(row, current + delta);
    }

    onInputChange(row) {
      const input = row.querySelector('[data-qty-input]');
      const current = parseInt(input.value, 10);
      this.setRowQty(row, isNaN(current) ? 0 : current);
    }

    setRowQty(row, qty) {
      const variantId = Number(row.dataset.variantId);
      const tracks = row.dataset.tracksInventory === 'true';
      const inventoryQty = parseInt(row.dataset.inventoryQuantity, 10) || 0;
      const cartQty = this.cartQtys[variantId] || 0;
      const available = tracks ? Math.max(0, inventoryQty - cartQty) : Infinity;

      let next = Math.max(0, qty);
      let exceeded = false;
      if (tracks && next > available) {
        next = available;
        exceeded = qty > available;
      }

      const input = row.querySelector('[data-qty-input]');
      input.value = next;
      this.selections[variantId] = next;

      this.updateRowState(row, { tracks, inventoryQty, cartQty, selection: next, exceeded });
      this.recomputeTotal();
      this.updateAddBtnState();
    }

    updateRowState(row, { tracks, inventoryQty, cartQty, selection, exceeded }) {
      const minus = row.querySelector('[data-qty-minus]');
      const plus = row.querySelector('[data-qty-plus]');
      const input = row.querySelector('[data-qty-input]');
      const badge = row.querySelector('[data-stock-badge]');
      const rowError = row.querySelector('[data-row-error]');

      // A row that is out at render stays out. An 'ok' row whose remaining stock is all in the
      // cart is 'maxed', not out of stock — it must not offer a restock signup.
      let state = this.initialStates[row.dataset.variantId] || 'ok';
      let badgeText = 'Out of stock';

      if (state === 'ok') {
        if (tracks && Math.max(0, inventoryQty - cartQty) <= 0) {
          state = 'maxed';
          badgeText = 'Max in cart';
        } else {
          badgeText = 'In stock';
        }
      }

      row.dataset.stockState = state;
      if (badge) badge.textContent = badgeText;

      if (rowError) {
        rowError.hidden = true;
        rowError.textContent = '';
      }

      const disable = state !== 'ok';
      [minus, plus, input].forEach((el) => { if (el) el.disabled = disable; });

      if (plus && tracks && !disable) {
        plus.disabled = selection >= Math.max(0, inventoryQty - cartQty);
      }
    }

    recomputeAll() {
      this.rows.forEach((row) => {
        const variantId = Number(row.dataset.variantId);
        const selection = this.selections[variantId] || 0;
        const tracks = row.dataset.tracksInventory === 'true';
        const inventoryQty = parseInt(row.dataset.inventoryQuantity, 10) || 0;
        const cartQty = this.cartQtys[variantId] || 0;
        this.updateRowState(row, { tracks, inventoryQty, cartQty, selection, exceeded: false });
      });
      this.recomputeTotal();
      this.updateAddBtnState();
    }

    recomputeTotal() {
      if (!this.totalEl) return;
      let totalCents = 0;
      this.variants.forEach((v) => {
        const qty = this.selections[v.variantId] || 0;
        totalCents += qty * (v.price || 0);
      });
      this.totalEl.textContent = BulkOrderConfigurator.formatMoney(totalCents);

      // Keep the Afterpay placement bound to the running total (see afterpay-placement.js).
      const afterpayPlacement = this.querySelector('[data-afterpay-placement]');
      if (afterpayPlacement) {
        document.dispatchEvent(new CustomEvent('on:afterpay:amount-change', {
          detail: {
            targetId: afterpayPlacement.dataset.afterpayPlacement,
            amountCents: totalCents
          }
        }));
      }
    }

    updateAddBtnState() {
      if (!this.addBtn) return;
      const totalQty = Object.values(this.selections).reduce((sum, q) => sum + q, 0);
      this.addBtn.disabled = totalQty <= 0;
    }

    async handleAdd() {
      if (this.addBtn.disabled) return;

      const items = Object.entries(this.selections)
          .filter(([, qty]) => qty > 0)
          .map(([id, qty]) => ({ id: Number(id), quantity: qty }));
      if (items.length === 0) return;

      this.setErrorState(null);
      this.addBtn.classList.add('is-loading');
      this.addBtn.disabled = true;

      const cartDrawer = document.querySelector('cart-drawer');
      const quickAddDrawer = this.closest('quick-add-drawer');
      const formData = new FormData();
      items.forEach((it, i) => {
        formData.append(`items[${i}][id]`, it.id);
        formData.append(`items[${i}][quantity]`, it.quantity);
      });

      let sections = 'cart-icon-bubble';
      if (cartDrawer) {
        const cartDrawerSectionId = cartDrawer.closest('.shopify-section')?.id?.replace('shopify-section-', '');
        if (cartDrawerSectionId) sections += `,${cartDrawerSectionId}`;
      }
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);

      try {
        const res = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await res.json();

        if (!res.ok) {
          const msg = data.description || data.message || 'Could not add to cart.';
          this.setErrorState(msg);
          throw new Error(msg);
        }

        if (data.sections && data.sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) bubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        if (quickAddDrawer && typeof quickAddDrawer.addedToCart === 'function') {
          quickAddDrawer.addedToCart();
        }

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data, !quickAddDrawer);
        }

        this.variants.forEach((v) => { this.selections[v.variantId] = 0; });
        this.rows.forEach((row) => {
          const input = row.querySelector('[data-qty-input]');
          if (input) input.value = 0;
        });
        await this.refreshCart();
        this.recomputeAll();
      } catch (err) {
        console.log('bulk-order-configurator add failed:', err); // eslint-disable-line
      } finally {
        this.addBtn.classList.remove('is-loading');
        this.updateAddBtnState();
      }
    }

    setErrorState(msg) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !msg;
      this.errorEl.textContent = msg || '';
    }

    static formatMoney(cents) {
      const format = (window.theme && window.theme.settings && window.theme.settings.moneyWithCurrencyFormat) || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
    }
  }

  customElements.define('bulk-order-configurator', BulkOrderConfigurator);
}
